import React, { useCallback, useMemo } from 'react';
import ReactFlow, {
    Node,
    Edge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    NodeTypes,
    BackgroundVariant,
    MarkerType,
    FitViewOptions,
} from 'reactflow';
import Dagre from '@dagrejs/dagre';

import { OrganizationChartData } from '../../models/Hierarchy';
import HierarchyNode, { HierarchyNodeData } from './HierarchyNode';

// ─── Layout constants ─────────────────────────────────────────────────────────
const NODE_WIDTH  = 248;
const NODE_HEIGHT = 110;

// ─── Dagre layout ─────────────────────────────────────────────────────────────
const getLayoutedElements = (rawNodes: Node[], rawEdges: Edge[]) => {
    const g = new Dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', nodesep: 50, ranksep: 70 });

    rawNodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
    rawEdges.forEach((e) => g.setEdge(e.source, e.target));

    Dagre.layout(g);

    return {
        nodes: rawNodes.map((n) => {
            const pos = g.node(n.id);
            return { ...n, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } };
        }),
        edges: rawEdges,
    };
};

// ─── Tree → flat lists ────────────────────────────────────────────────────────
function flattenTree(
    data: OrganizationChartData,
    nodes: Node[],
    edges: Edge[],
    depth = 0,
    selectedId: string | null = null,
): void {
    const childCount = data.children.length;

    nodes.push({
        id: data.id as string,
        type: 'hierarchyNode',
        position: { x: 0, y: 0 },
        data: {
            ...data.data,
            depth,
            childCount,
            isSelected: data.id === selectedId,
        } as HierarchyNodeData,
    });

    data.children.forEach((child) => {
        edges.push({
            id: `${data.id}→${child.id}`,
            source: data.id as string,
            target: child.id as string,
            type: 'smoothstep',
            markerEnd: {
                type: MarkerType.ArrowClosed,
                color: 'var(--cds-accent)',
                width: 11,
                height: 11,
            },
            style: {
                stroke: 'var(--cds-accent)',
                strokeWidth: 1.5,
                opacity: 0.55,
            },
        });
        flattenTree(child, nodes, edges, depth + 1, selectedId);
    });
}

// ─── Node types ───────────────────────────────────────────────────────────────
const nodeTypes: NodeTypes = { hierarchyNode: HierarchyNode };

const FIT_VIEW_OPTIONS: FitViewOptions = { padding: 0.18, duration: 400 };

// ─── Props ────────────────────────────────────────────────────────────────────
interface HierarchyFlowChartProps {
    dataSource: OrganizationChartData;
    selectedId: string | null;
    onNodeClick: (data: OrganizationChartData) => void;
    onPaneClick: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
const HierarchyFlowChartInner: React.FC<HierarchyFlowChartProps> = ({
    dataSource,
    selectedId,
    onNodeClick,
    onPaneClick,
}) => {
    // Recompute layout when data or selection changes
    const { nodes: layoutNodes, edges: layoutEdges } = useMemo(() => {
        const rawNodes: Node[] = [];
        const rawEdges: Edge[] = [];
        flattenTree(dataSource, rawNodes, rawEdges, 0, selectedId);
        return getLayoutedElements(rawNodes, rawEdges);
    }, [dataSource, selectedId]);

    const [nodes, , onNodesChange] = useNodesState(layoutNodes);
    const [edges, , onEdgesChange] = useEdgesState(layoutEdges);

    // Find the matching OrganizationChartData for a clicked node id
    const findNodeData = useCallback((
        root: OrganizationChartData,
        id: string,
    ): OrganizationChartData | null => {
        if (root.id === id) return root;
        for (const child of root.children) {
            const hit = findNodeData(child, id);
            if (hit) return hit;
        }
        return null;
    }, []);

    const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
        const found = findNodeData(dataSource, node.id);
        if (found) onNodeClick(found);
    }, [dataSource, findNodeData, onNodeClick]);

    return (
        <ReactFlow
            // Re-mount (and re-fit) when data reference changes
            proOptions={{ hideAttribution: true }}
            key={dataSource.id as string}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={FIT_VIEW_OPTIONS}
            minZoom={0.15}
            maxZoom={2.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            zoomOnDoubleClick={false}
            deleteKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
        >
            <Background
                variant={BackgroundVariant.Dots}
                color="var(--cds-border)"
                gap={22}
                size={1.2}
            />
            <Controls
                showInteractive={false}
                fitViewOptions={FIT_VIEW_OPTIONS}
                className="hf-controls"
            />
            <MiniMap
                nodeColor={() => 'var(--cds-accent)'}
                maskColor="rgba(0,0,0,0.45)"
                className="hf-minimap"
                pannable
                zoomable
            />
        </ReactFlow>
    );
};

export default React.memo(HierarchyFlowChartInner);
